import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import RefreshIcon from "@mui/icons-material/Refresh";
import { confirmAlert } from "react-confirm-alert";
import { findPCP } from "./api-presenca-capitulo";

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: "20px",
    margin: "auto",
    width: "100%",
    flexGrow: 1,
    transition: "box-shadow 0.2s ease-in-out",
    "&:hover": {
      boxShadow:
        "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
  },
  bimestreLabel: {
    marginBottom: 4,
  },
  valueRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
    marginBottom: 6,
  },
  progressBar: {
    borderRadius: 4,
    height: 6,
  },
}));

const BIMESTRES = [
  {
    label: "1º Bimestre",
    capituloKey: "capitulo1",
    planejamentoKey: "planejamento1",
  },
  {
    label: "2º Bimestre",
    capituloKey: "capitulo2",
    planejamentoKey: "planejamento2",
  },
  {
    label: "3º Bimestre",
    capituloKey: "capitulo3",
    planejamentoKey: "planejamento3",
  },
  {
    label: "4º Bimestre",
    capituloKey: "capitulo4",
    planejamentoKey: "planejamento4",
  },
];

const EMPTY_PCP = {
  tdp: "",
  capitulo1: 0,
  capitulo2: 0,
  capitulo3: 0,
  capitulo4: 0,
  planejamento1: 0,
  planejamento2: 0,
  planejamento3: 0,
  planejamento4: 0,
};

function getProgressColor(percent) {
  if (percent >= 90) return "success";
  if (percent >= 50) return "warning";
  return "error";
}

export default function PresencaCapituloPlanejamento({ tdp }) {
  const classes = useStyles();
  const [pcp, setPcp] = useState(EMPTY_PCP);
  const [loading, setLoading] = useState(false);

  const showFetchError = useCallback((mensagem) => {
    confirmAlert({
      title: "Erro",
      message: mensagem,
      buttons: [{ label: "Ok", onClick: () => {} }],
    });
  }, []);

  const fetchPCP = useCallback(() => {
    if (!tdp?.codigo) return;

    setLoading(true);
    findPCP(tdp.codigo)
      .then((data) => {
        if (data?.error) {
          if (data.message === "Erro: Bimestre nao cadastrado para a serie") {
            showFetchError(
              "Bimestre não cadastrado para esta série. Tabela Presença Capítulo Planejamento ficará sem data.",
            );
          } else {
            showFetchError(
              "Não foi possível carregar os dados de presença/capítulo.",
            );
          }
          return;
        }
        setPcp(data);
      })
      .catch(() => {
        showFetchError(
          "Falha de comunicação ao buscar os dados. Tente novamente.",
        );
      })
      .finally(() => setLoading(false));
  }, [tdp, showFetchError]);

  useEffect(() => {
    fetchPCP();
  }, [fetchPCP]);

  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        <div className={classes.header}>
          <Typography className={classes.title}>
            Planejamento Capítulo
          </Typography>
          <Tooltip title="Atualizar">
            <span>
              <IconButton
                size="small"
                onClick={fetchPCP}
                disabled={loading}
                aria-label="Atualizar dados de planejamento"
              >
                <RefreshIcon
                  fontSize="small"
                  className={loading ? "rotating" : undefined}
                />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <Grid container spacing={3}>
          {BIMESTRES.map(({ label, capituloKey, planejamentoKey }) => {
            const capitulo = pcp[capituloKey] ?? 0;
            const planejamento = pcp[planejamentoKey] ?? 0;
            const percent =
              planejamento > 0
                ? Math.min(100, Math.round((capitulo / planejamento) * 100))
                : 0;

            return (
              <Grid size={3} key={label}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.bimestreLabel}
                >
                  {label}
                </Typography>

                {loading ? (
                  <>
                    <Skeleton width="60%" height={28} />
                    <Skeleton width="100%" height={6} />
                  </>
                ) : (
                  <>
                    <div className={classes.valueRow}>
                      <Typography variant="body1" fontWeight={600}>
                        {capitulo}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        / {planejamento}
                      </Typography>
                    </div>
                    <LinearProgress
                      className={classes.progressBar}
                      variant="determinate"
                      value={percent}
                      color={getProgressColor(percent)}
                    />
                  </>
                )}
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
}
